import { createController, createApplePay, createGooglePay } from '@ryftpay/web';
import { getCsrfToken } from '../../utils.js';

class RyftExpressPay {
	constructor(
		publicKey,
		affiliateId,
		productVariantId,
		shippingOptions,
		paymentDisplayName,
		productVariantPrice,
		walletConfig,
	) {
		this.debug = false;

		this.publicKey = publicKey;
		this.affiliateId = affiliateId;
		this.productVariantId = productVariantId;
		this.shippingOptions = shippingOptions || [];
		this.paymentDisplayName = paymentDisplayName || 'Cosmetify';
		this.baseAmount = productVariantPrice;
		this.walletConfig = walletConfig || {};

		this.controller = null;
		this.applePay = null;
		this.googlePay = null;
		this.shippingOptionId = null;
		this.deliveryInfo = null;
		this.returnUrl = document.getElementById('ryftPaymentInfo')?.getAttribute('data-return-url') || window.location.href;
	}

	async init() {
		if (!this.publicKey) {
			(this.debug) && console.error('Ryft public key is missing.');
			return false;
		}

		this.controller = createController({
			publicKey: this.publicKey,
		});

		this.controller.on('error', (event) => {
			(this.debug) && console.error('Ryft controller error:', event?.message || event);
			this.setErrorMessage(document.body, event?.message || 'Payment failure: wallet setup failed.');
		});

		this.controller.setBeforePaymentAttemptHandler(async () => {
			try {
				const payload = await this.buildBeforePaymentAttemptPayload();
				return { type: 'success', data: payload };
			} catch (error) {
				(this.debug) && console.error('beforePaymentAttempt failed:', error);
				return {
					type: 'error',
					error: {
						message: error?.message || 'Unable to prepare payment.',
					},
				};
			}
		});

		this.controller.on('paymentSessionResult', async (event) => {
			await this.handlePaymentSessionResult(event);
		});

		return true;
	}

	async createApplePayButton(containerId) {
		if (!this.controller) return false;
		const containerSelector = `#${containerId}`;
		const container = document.querySelector(containerSelector);
		if (!container) return false;

		try {
			this.applePay = createApplePay(this.controller, {
				amount: this.baseAmount,
				currency: 'GBP',
				label: this.paymentDisplayName,
				requestPayerName: true,
				requestPayerEmail: true,
				requestShipping: true,
				...this.walletConfig,
			});

			this.applePay.on('shippingAddressChange', (event) => this.handleShippingAddressChange(event));
			this.applePay.on('shippingOptionChange', (event) => this.handleShippingOptionChange(event));
			this.applePay.on('error', (event) => {
				(this.debug) && console.error('Apple Pay error:', event?.message || event);
			});

			await this.applePay.mount(containerSelector);
			return true;
		} catch (error) {
			(this.debug) && console.warn('Apple Pay is not available:', error);
			return false;
		}
	}

	async createGooglePayButton(containerId) {
		if (!this.controller) return false;
		const containerSelector = `#${containerId}`;
		const container = document.querySelector(containerSelector);
		if (!container) return false;

		try {
			this.googlePay = createGooglePay(this.controller, {
				amount: this.baseAmount,
				currency: 'GBP',
				label: this.paymentDisplayName,
				requestPayerName: true,
				requestPayerEmail: true,
				requestShipping: true,
				...this.walletConfig,
			});

			this.googlePay.on('shippingAddressChange', (event) => this.handleShippingAddressChange(event));
			this.googlePay.on('shippingOptionChange', (event) => this.handleShippingOptionChange(event));
			this.googlePay.on('error', (event) => {
				(this.debug) && console.error('Google Pay error:', event?.message || event);
			});

			await this.googlePay.mount(containerSelector);
			return true;
		} catch (error) {
			(this.debug) && console.warn('Google Pay is not available:', error);
			return false;
		}
	}

	getCountryId(postalCode, countryCode) {
		const countryCodeIdMap = { GB: 1, NI: 13, SH: 15, CI: 18 };
		return this.isChannelIslands(postalCode, countryCode) ? countryCodeIdMap.CI
			: this.isNorthernIreland(postalCode, countryCode) ? countryCodeIdMap.NI
			: this.isScottishHighlands(postalCode, countryCode) ? countryCodeIdMap.SH
			: countryCodeIdMap.GB;
	}

	isChannelIslands(postalCode, countryCode) {
		if (countryCode !== 'GB') return false;
		const normalized = (postalCode || '').toLowerCase();
		return normalized.startsWith('je') || normalized.startsWith('gy');
	}

	isScottishHighlands(postalCode, countryCode) {
		if (countryCode !== 'GB') return false;
		return /^HS|IV|KA27|KW|PA20|PA21|PA38|PH41|PH42|ZE/.test((postalCode || '').toUpperCase());
	}

	isNorthernIreland(postalCode, countryCode) {
		if (countryCode !== 'GB') return false;
		return (postalCode || '').toLowerCase().startsWith('bt');
	}

	getAvailableShippingOptions(postalCode, countryCode) {
		let availableShippingOptions = [];
		(countryCode === 'GB') && (availableShippingOptions = this.shippingOptions.filter((opt) => opt.countries.some((c) => c.id === '1')));
		this.isChannelIslands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter((opt) => opt.countries.some((c) => c.id === '18')));
		this.isScottishHighlands(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter((opt) => opt.countries.some((c) => c.id === '15')));
		this.isNorthernIreland(postalCode, countryCode) && (availableShippingOptions = this.shippingOptions.filter((opt) => opt.countries.some((c) => c.id === '13')));
		return availableShippingOptions.map((opt) => ({
			id: opt.id,
			label: opt.label,
			detail: opt.detail,
			amount: opt.amount,
		}));
	}

	validateShippingAddress(countryCode, countryId, postalCode) {
		if (!countryCode || !countryId || !postalCode) return false;
		if (countryId === 18 && this.isChannelIslands(postalCode, countryCode)) return true;
		if (countryId === 15 && this.isScottishHighlands(postalCode, countryCode)) return true;
		if (countryId === 13 && this.isNorthernIreland(postalCode, countryCode)) return true;
		if (countryCode === 'GB' && countryId === 1) return true;
		return false;
	}

	splitName(fullName) {
		if (!fullName) return { firstName: '', lastName: '' };
		const parts = fullName.trim().split(' ');
		return {
			firstName: parts[0] || '',
			lastName: parts.slice(1).join(' ') || '',
		};
	}

	handleShippingAddressChange(event) {
		const address = event?.shippingAddress || event?.address || {};

		this.deliveryInfo = {
			country: address.country || 'GB',
			postalCode: address.postalCode || address.postal_code || '',
			addressLine1: address.addressLine1 || (Array.isArray(address.addressLine) ? address.addressLine[0] : '') || address.line1 || '',
			addressLine2: address.addressLine2 || (Array.isArray(address.addressLine) ? address.addressLine[1] : '') || address.line2 || '',
			city: address.city || address.locality || '',
			region: address.region || address.state || '',
			recipient: address.recipient || address.name || '',
			phone: address.phone || '',
		};

		const options = this.getAvailableShippingOptions(this.deliveryInfo.postalCode, this.deliveryInfo.country);
		if (!options.length) {
			event?.updateWith?.({
				shippingOptions: [],
				status: 'invalid_shipping_address',
			});
			return;
		}

		this.shippingOptionId = options[0].id;
		const newTotal = this.baseAmount + options[0].amount;

		event?.updateWith?.({
			status: 'success',
			shippingOptions: options,
			total: {
				label: this.paymentDisplayName,
				amount: newTotal,
			},
		});
	}

	handleShippingOptionChange(event) {
		if (!this.deliveryInfo) return;

		const options = this.getAvailableShippingOptions(this.deliveryInfo.postalCode, this.deliveryInfo.country);
		this.shippingOptionId = event?.shippingOption?.id || event?.id || this.shippingOptionId;
		const selected = options.find((opt) => opt.id === this.shippingOptionId) || options[0];
		const newTotal = this.baseAmount + (selected ? selected.amount : 0);

		event?.updateWith?.({
			status: 'success',
			shippingOptions: options,
			total: {
				label: this.paymentDisplayName,
				amount: newTotal,
			},
		});
	}

	async buildBeforePaymentAttemptPayload() {
		if (!this.deliveryInfo) {
			throw new Error('Shipping address was not provided.');
		}

		const countryId = this.getCountryId(this.deliveryInfo.postalCode, this.deliveryInfo.country);
		if (!this.validateShippingAddress(this.deliveryInfo.country, countryId, this.deliveryInfo.postalCode)) {
			throw new Error('Shipping address is outside UK.');
		}

		const basketUpdateResponse = await this.updateBasket({ product_variant_id: this.productVariantId });
		if (!basketUpdateResponse.success) {
			throw new Error('Unable to update basket.');
		}

		const setDeliveryOk = await this.setDeliveryInfoOnOrder({ country: countryId, [this.affiliateId]: this.shippingOptionId });
		if (!setDeliveryOk) {
			throw new Error('Unable to set delivery information.');
		}

		const basketOk = await this.basketChecker(basketUpdateResponse.order_id, [this.productVariantId]);
		if (!basketOk) {
			throw new Error('Basket has a problem.');
		}

		return {
			orderId: basketUpdateResponse.order_id,
			amount: this.baseAmount,
			currency: 'GBP',
			returnUrl: this.returnUrl,
			customerDetails: {},
			shippingDetails: {
				address: {
					lineOne: this.deliveryInfo.addressLine1,
					lineTwo: this.deliveryInfo.addressLine2,
					city: this.deliveryInfo.city,
					region: this.deliveryInfo.region,
					postalCode: this.deliveryInfo.postalCode,
					country: this.deliveryInfo.country,
				},
			},
		};
	}

	async handlePaymentSessionResult(event) {
		try {
			const paymentSession = event?.paymentSession;
			if (!paymentSession) {
				this.setErrorMessage(document.body, 'Payment failure: Missing payment session result.');
				return;
			}

			if (paymentSession.status !== 'Approved' && paymentSession.status !== 'Captured') {
				this.setErrorMessage(document.body, event?.userFacingErrorMessage || 'Payment was not successful.');
				return;
			}

			window.location.href = '/basket/confirmation/';
		} catch (error) {
			console.error('Failed to process Ryft express payment result:', error);
			this.setErrorMessage(document.body, 'Payment failure: An unexpected error occurred.');
		}
	}

	async updateBasket(data) {
		const response = await fetch('/express-checkout/basket/update/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-CSRFToken': getCsrfToken(),
			},
			body: JSON.stringify(data),
		});
		return await response.json();
	}

	async basketChecker(orderId, pvids) {
		const response = await fetch(`/basket/api/basket-checker/${orderId}/`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-CSRFToken': getCsrfToken(),
			},
			body: JSON.stringify({ pvids: pvids }),
		});
		const result = await response.json();
		if (!result.happy_check) {
			console.warn('Basket check failed:', result);
			return false;
		}
		if (result.order_processed) {
			console.warn('Order has already been processed. Redirecting to confirmation page.');
			return false;
		}
		return true;
	}

	async setDeliveryInfoOnOrder(deliveryInfo) {
		const response = await fetch('/basket/api/set-delivery-info/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json; charset=utf-8',
				'X-CSRFToken': getCsrfToken(),
			},
			body: JSON.stringify(deliveryInfo),
		});
		return response.ok;
	}

	setErrorMessage(container, message) {
		const errorMessageDiv = container.querySelector('.express-error-message');
		if (errorMessageDiv) {
			errorMessageDiv.textContent = message;
			errorMessageDiv.style.display = 'block';
		}
	}

	static async initFromContainer(container) {
		const publicKey = container.getAttribute('data-public-key');
		const affiliateId = parseInt(container.querySelector('.shipping-option')?.getAttribute('data-affiliate-id'), 10);
		const productVariantId = parseInt(container.getAttribute('data-product-variant-id'), 10);
		const productName = container.getAttribute('data-product-name');
		const productVariantName = container.getAttribute('data-product-variant-name');
		const productVariantPrice = parseInt(container.getAttribute('data-product-variant-price'), 10);
		const productVariantInStock = container.getAttribute('data-in-stock') === 'True';
		const numberDeliveryOptions = parseInt(container.getAttribute('data-number-delivery-options'), 10);
		const paymentDisplayName = productVariantName ? `${productName} - ${productVariantName}` : productName;

		if (!productVariantInStock || numberDeliveryOptions === 0) {
			return;
		}

		const shippingOptions = [];
		container.querySelectorAll('.shipping-option').forEach((option) => {
			shippingOptions.push({
				id: option.getAttribute('data-service-id'),
				label: option.getAttribute('data-label'),
				detail: option.getAttribute('data-detail'),
				amount: parseInt(option.getAttribute('data-gross-price'), 10),
				countries: Array.from(option.querySelectorAll('.shipping-country')).map((countryDiv) => ({
					id: countryDiv.getAttribute('data-country-id'),
					code: countryDiv.getAttribute('data-country-code'),
					name: countryDiv.getAttribute('data-country-name'),
				})),
			});
		});

		const expressPay = new RyftExpressPay(
			publicKey,
			affiliateId,
			productVariantId,
			shippingOptions,
			paymentDisplayName,
			productVariantPrice,
			{},
		);

		const initialized = await expressPay.init();
		if (!initialized) return;

		const applePayMounted = await expressPay.createApplePayButton(`ryft-apple-pay-${productVariantId}`);
		const googlePayMounted = await expressPay.createGooglePayButton(`ryft-google-pay-${productVariantId}`);

		if (!applePayMounted && !googlePayMounted) {
			const expressPayContainer = document.getElementById('expressPay');
			if (expressPayContainer) {
				expressPayContainer.style.display = 'none';
			}
		}
	}
}

export default RyftExpressPay;
